/**
 * Recovers from stale-deploy chunk-load failures.
 *
 * After a deploy, asset filename hashes change. A user holding a cached
 * index.html dynamically imports route chunks that no longer exist; the
 * server's SPA fallback then returns index.html (text/html) instead of
 * JavaScript. Vite emits a `vite:preloadError` event for these failures
 * (ORDER-THISWAY-10 / 11 / 1T / 1Z).
 *
 * We reload the page once so the browser fetches a fresh index.html.
 * A sessionStorage timestamp guards against an infinite reload loop: if a
 * reload already happened within RELOAD_WINDOW_MS, we do NOT reload again
 * and do NOT suppress the error — a second failure means the chunk is
 * genuinely broken, not stale, and should stay visible in Sentry.
 */
const RELOAD_FLAG_KEY = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 10_000;

// sessionStorage access can throw a SecurityError in private-browsing modes
// (notably Safari private mode). Wrap it so the guard never crashes — at
// worst it falls back to attempting one reload per page load.
function readReloadTimestamp() {
  try {
    return Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
  } catch {
    return 0;
  }
}

function writeReloadTimestamp(ts) {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(ts));
  } catch {
    // best-effort — if storage is unavailable, the loop guard degrades
    // gracefully to one reload attempt per page load.
  }
}

export function installChunkReloadGuard() {
  window.addEventListener('vite:preloadError', (event) => {
    const last = readReloadTimestamp();
    const now = Date.now();

    if (now - last < RELOAD_WINDOW_MS) {
      // Already reloaded recently — the chunk is genuinely broken.
      // Do NOT preventDefault: let Vite re-throw so Sentry records it.
      return;
    }

    // Stale deploy: suppress Vite's re-throw and reload to get a fresh
    // index.html with up-to-date asset hashes.
    event.preventDefault();
    writeReloadTimestamp(now);
    window.location.reload();
  });
}
